import { useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { track, trackError } from '@/lib/analytics'
import { usePollsAccess } from '@/config/polls'
import { fetchLabMembership, type LabMembership } from '@/api/labMembership'

/**
 * v3.1a §2.0 + §4.5 + §7.5 + R1/R2 (v3.1a v3.1b) + backend contract 2026-07-23.
 *
 * Один вызов на entry в /polls в ЛК — не polling. Bypass кэша через
 * `X-Lab-Recheck: true` header при явном refetch (visibilitychange /
 * пост-клик по «Подписаться и продолжить» / webview 30s fallback R2).
 *
 * Два уровня fail-open — оба конвертируются в `isMember=true`:
 * 1. **Frontend fail-open** — если `fetchLabMembership` вернул `null`
 *    (сеть/CORS/timeout/JSON-parse) → считаем `isMember=true` + эмитим
 *    `poll_lab_check_failed` с `reason='network'|'5xx'`.
 * 2. **Backend fail-open** — если бэк уже отдал `{ isMember: true,
 *    checkFailed: true, cacheTtlSec: 60 }` (MAX API upstream был недоступен
 *    у него) → уважаем `isMember=true` + эмитим `poll_lab_check_failed` с
 *    `reason='backend-fail-open'`. TTL кеша сжимается до 60с — чтобы
 *    false-positive шлейф после восстановления MAX API не тянулся 5 мин.
 *
 * `staleTime` динамически берётся из `response.cacheTtlSec * 1000`
 * (fallback 5 мин).
 */
const STALE_TIME_HAPPY_MS = 5 * 60 * 1000
const STALE_TIME_FAIL_OPEN_MS = 60 * 1000

export function useLabMembership() {
  const allowed = usePollsAccess()
  const qc = useQueryClient()

  const query = useQuery<LabMembership | null>({
    queryKey: ['polls-lab-membership'],
    queryFn: () => fetchLabMembership({ bypassCache: false }),
    enabled: allowed,
    // Динамический staleTime: react-query v5 умеет function form, читает из
    // текущего значения query.state.data. Backend fail-open → 60с, иначе 5мин.
    staleTime: (q) => {
      const data = q.state.data
      return data?.checkFailed || data == null ? STALE_TIME_FAIL_OPEN_MS : STALE_TIME_HAPPY_MS
    },
    meta: {
      onError: (e: unknown) => trackError(e, 'polls-lab-membership'),
    },
  })

  // Fail-open + observability
  const membership: LabMembership | null = query.data ?? null
  const effectiveIsMember = membership?.isMember ?? true // frontend fail-open

  useEffect(() => {
    // Frontend fail-open — fetch вернул null (сеть/JSON-parse/CORS)
    if (query.isError || (query.isFetched && !membership)) {
      track('poll_lab_check_failed', {
        level: 'warn',
        reason: query.isError ? 'network' : 'null-response',
      })
      return
    }
    // Backend fail-open — бэк явно сказал что MAX API upstream был недоступен
    if (membership?.checkFailed === true) {
      track('poll_lab_check_failed', {
        level: 'warn',
        reason: 'backend-fail-open',
        cacheTtlSec: membership.cacheTtlSec,
      })
    }
  }, [query.isError, query.isFetched, membership])

  /**
   * Явный bypass-кэша refetch. Возвращает свежее значение LabMembership
   * (или null при frontend fail-open), чтобы вызывающий мог показать
   * визуальный фидбек: «проверили — вы всё ещё не подписаны», спиннер и т.д.
   */
  const recheck = useCallback(async (): Promise<LabMembership | null> => {
    track('poll_gate_refresh_clicked', { source: 'manual' })
    const fresh = await fetchLabMembership({ bypassCache: true })
    qc.setQueryData(['polls-lab-membership'], fresh)
    return fresh
  }, [qc])

  return {
    isLoading: query.isLoading,
    isMember: effectiveIsMember,
    labChannelDeepLink: membership?.labChannelDeepLink ?? '',
    /** Явный bypass-кэша refetch. Возвращает свежий result — компонент решает как реагировать. */
    recheck,
    /** Сырое значение (для отладки — включая fail-open ветку). */
    raw: membership,
  }
}
